import { api } from "./api";
import { fmtDate } from "./format";

declare global {
  interface Window {
    jspdf: any;
    GENDOCS: any[];
    genDevisPDF: (o: any) => void;
    genProformaStd: (o: any) => void;
    genProformaBluemega: (o: any) => void;
  }
}

interface Captured {
  blob: Blob;
  filename: string;
}

function withCapture(fn: () => void, reserve = 0): Captured | null {
  const jsPDF = window.jspdf?.jsPDF;
  if (!jsPDF) throw new Error("jsPDF non chargé");
  const origSave = jsPDF.API.save;
  const origAutoTable = jsPDF.API.autoTable;
  let captured: Captured | null = null;
  jsPDF.API.autoTable = function (this: any, opts: any) {
    const res = origAutoTable.call(this, { ...opts, rowPageBreak: "avoid", margin: { ...(opts.margin || {}), bottom: 64 } });
    const pageH = this.internal.pageSize.getHeight();
    if (reserve > 0 && this.lastAutoTable && pageH - this.lastAutoTable.finalY < reserve) {
      this.addPage();
      this.lastAutoTable.finalY = 46;
    }
    return res;
  };
  jsPDF.API.save = function (this: any, filename: string) {
    const nb = this.internal.getNumberOfPages();
    if (nb > 1) {
      const pw = this.internal.pageSize.getWidth();
      const ph = this.internal.pageSize.getHeight();
      for (let i = 1; i <= nb; i++) {
        this.setPage(i);
        this.setFont("helvetica", "normal");
        this.setFontSize(7.5);
        this.setTextColor(110, 122, 116);
        this.text(`Page ${i}/${nb}`, pw - 24, ph - 10, { align: "right" });
      }
    }
    const blob = this.output("blob");
    captured = { blob, filename };
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  try {
    fn();
  } finally {
    if (origSave === undefined) delete jsPDF.API.save;
    else jsPDF.API.save = origSave;
    if (origAutoTable === undefined) delete jsPDF.API.autoTable;
    else jsPDF.API.autoTable = origAutoTable;
  }
  return captured;
}

function devisReserve(o: any): number {
  const rows = (o.t.rg > 0 ? 3 : 1) + 1 + (o.t.retenue > 0 ? 1 : 0) + 1;
  let r = 96 + rows * 17;
  if (o.note) r += 22 + Math.ceil(String(o.note).length / 90) * 11;
  if (o.conditions && o.conditions.length) r += 37 + o.conditions.length * 12;
  return r;
}

function proformaReserve(o: any): number {
  const tl = (o.t.rg > 0 ? 3 : 1) + 1 + (o.t.retenue > 0 ? 1 : 0);
  let r = 176 + tl * 15;
  if (o.infoFrs) r += 55;
  if (o.note) r += 41 + Math.ceil(String(o.note).length / 90) * 11;
  if (!o.noBank) r += 80 + (o.E && o.E.mentions ? o.E.mentions.length : 0) * 12;
  return r;
}

export function buildT(d: any): any {
  const ht = +d.total_ht || 0;
  const rg = +d.total_remise || 0;
  return {
    net: ht + rg,
    rg,
    remisePct: +d.remise_globale_pct || 0,
    ht,
    tva: +d.total_tva || 0,
    tvaRate: +d.tva_pct || 0,
    ttc: +d.total_ttc || 0,
    retenue: +d.total_retenue || 0,
    retenuePct: +d.retenue_pct || 0,
    napayer: +d.net_a_payer || 0,
  };
}

export function buildLignes(lignes: any[], tvaPct?: number): any[] {
  return (lignes || []).map((l) => ({
    type: l.type || "ligne",
    d: l.designation || "",
    q: +l.qte || 0,
    unite: l.unite || "",
    pu: +l.prix_unitaire || 0,
    rem: +l.remise_pct || 0,
    tva: tvaPct !== undefined ? tvaPct : +l.tva_pct || 0,
  }));
}

export function clientAddr(org: any): string {
  if (!org) return "";
  return [org.adresse, org.complement, [org.code_postal, org.ville].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join("\n");
}

export function buildE(entite: any): any {
  return {
    code: entite.code,
    nom: entite.nom,
    forme: entite.forme,
    cap: entite.capital,
    adr: entite.adresse || [],
    tel: entite.telephone,
    email: entite.email,
    web: entite.web,
    idfisc: entite.identifiants_fiscaux,
    tmpl: entite.template,
    mentions: entite.mentions && entite.mentions.length ? entite.mentions : undefined,
  };
}

export function pickBank(entite: any, devise: string): any {
  const banks = entite.banques || [];
  return banks.find((b: any) => b.devise === devise) || banks[0] || { titulaire: "", banque: "", iban: "", bic: "" };
}

async function archive(type: string, payload: any, meta: { devis_id?: string; bon_id?: string; entite_id?: string; tampon_paye?: boolean }, gen: (numero: string) => void, reserve = 0) {
  const { data: doc } = await api.post("/documents", { type, payload, ...meta });
  let captured: Captured | null = null;
  try {
    captured = withCapture(() => gen(String(doc.numero)), reserve);
    if (!captured) throw new Error("Le générateur PDF n'a produit aucun fichier");
  } catch (e) {
    await api.delete(`/documents/${doc.id}`).catch(() => {});
    throw e;
  }
  if (captured && !doc.storage_path) {
    const fd = new FormData();
    fd.append("file", captured.blob, captured.filename);
    await api.post(`/documents/${doc.id}/pdf`, fd);
  }
  return doc;
}

export async function genererDevisPDF(devis: any, clientOrg: any, clientFinalOrg: any) {
  const o = {
    num: devis.numero,
    objet: devis.objet || "",
    date: (devis.date_devis || "").slice(0, 10),
    produit: devis.produit_nom || "",
    sousProduit: devis.sous_produit_nom || "",
    clientNom: devis.client_nom || clientOrg?.nom || "",
    clientCode: devis.client_code || clientOrg?.code || "",
    clientAddr: clientAddr(clientOrg),
    clientPays: clientOrg?.pays || "",
    cfOnDoc: !!devis.cf_sur_document,
    clientFinal: devis.client_final_nom || clientFinalOrg?.nom || "",
    devise: devis.devise || "EUR",
    lignes: buildLignes(devis.lignes),
    t: buildT(devis),
    note: devis.note_libre_on ? devis.note_libre : "",
    remiseLabel: devis.remise_label || "Remise revendeur",
    conditions: devis.conditions || [],
  };
  return archive("devis", o, { devis_id: devis.id, entite_id: devis.entite_id || undefined }, () =>
    window.genDevisPDF(o), devisReserve(o)
  );
}

export async function genererProformaPDF(
  devis: any,
  clientOrg: any,
  entite: any,
  opts: { facture?: boolean; paid?: boolean }
) {
  const E = buildE(entite);
  const bank = pickBank(entite, devis.devise || "EUR");
  const build = (numero: string) => ({
    num: opts.facture ? numero : devis.numero,
    devisNum: devis.numero,
    objet: devis.objet || "",
    date: fmtDate(devis.date_devis),
    clientNom: devis.client_nom || clientOrg?.nom || "",
    clientCode: devis.client_code || clientOrg?.code || "",
    clientAddr: clientAddr(clientOrg),
    clientPays: clientOrg?.pays || "",
    clientFinal: devis.client_final_nom || "",
    cfOnDoc: !!devis.cf_sur_document,
    devise: devis.devise || "EUR",
    lignes: buildLignes(devis.lignes, +devis.tva_pct || 0),
    t: buildT(devis),
    note: devis.note_libre_on ? devis.note_libre : "",
    remiseLabel: devis.remise_label || "Remise revendeur",
    E,
    bank,
    paid: !!opts.paid,
    title: opts.facture ? "FACTURE" : "Facture pro-forma",
  });
  const type = opts.facture ? "facture" : "proforma";
  const o0 = build("");
  return archive(type, o0, { devis_id: devis.id, entite_id: entite.id, tampon_paye: !!opts.paid }, (numero) => {
    const o = build(numero);
    if (E.tmpl === "bluemega") window.genProformaBluemega(o);
    else window.genProformaStd(o);
  }, proformaReserve(o0));
}

export async function genererBonPDF(bon: any, fournisseurOrg: any, entite: any) {
  const E = buildE(entite);
  const devise = entite.code === "NA" ? (bon.devise === "TND" ? "TND" : "EUR") : "EUR";
  const bank = pickBank(entite, devise);
  const frsAddr = clientAddr(fournisseurOrg);
  const infoFrs = [
    bon.client_final_nom ? `Client final : ${bon.client_final_nom}` : "",
    bon.revendeur_nom ? `Revendeur : ${bon.revendeur_nom}` : "",
  ]
    .filter(Boolean)
    .join("   ·   ");
  const o = {
    num: bon.numero,
    devisNum: bon.devis_numero || "",
    refLabel: "N\u00b0 BON DE COMMANDE",
    refValue: bon.numero,
    addrLabel: "FOURNISSEUR",
    codeLabel: "CODE FOURNISSEUR",
    noBank: true,
    complLabel: "RÉF COMPLÉMENTAIRE",
    complValue: bon.client_final_nom || "—",
    remiseLabel: "Remise",
    objet: bon.objet || bon.devis_objet || "",
    date: fmtDate(bon.date),
    clientNom: bon.fournisseur_nom || fournisseurOrg?.nom || "",
    clientCode: bon.fournisseur_code || fournisseurOrg?.code || "",
    clientPays: fournisseurOrg?.pays || "",
    clientAddr: frsAddr,
    devise,
    lignes: buildLignes(bon.lignes, +bon.tva_pct || 0),
    t: buildT(bon),
    clientFinal: "",
    cfOnDoc: false,
    note: bon.note || "",
    infoFrs,
    E,
    bank,
    paid: !!bon.paye,
    title: "Bon de commande",
    totalLabel: (+bon.retenue_pct || 0) > 0 ? "NET À PAYER" : "TOTAL COMMANDE",
  };
  return archive("bon_commande", o, { bon_id: bon.id, entite_id: entite.id, tampon_paye: !!bon.paye }, () => {
    if (E.tmpl === "bluemega") window.genProformaBluemega(o);
    else window.genProformaStd(o);
  }, proformaReserve(o));
}
